import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import type { PropType } from 'vue';
import type {
	RowActionProps,
	RowActionEvents,
	RowActionUtils,
	RowActionType,
	RowActionSize,
	RowActionComponent,
} from '@/lib/types/rowAction'; /**
 * RowAction - Componente per singole azioni in tabelle
 *
 * Componente TypeScript per il rendering di singole azioni (bottoni/link)
 * utilizzato principalmente nelle tabelle via ActionsGroup.
 *
 * Features:
 * - ✅ TypeScript completo con tipi specifici
 * - ✅ Backward compatibility 100% preservata
 * - ✅ Element UI integration (el-button, tooltip)
 * - ✅ Navigation support (nuxt-link, router-link)
 * - ✅ Style variants (primary, danger, warning, success, info/default)
 * - ✅ Size variants (mini, small, medium)
 * - ✅ Circle mode per icone
 * - ✅ Loading e disabled states
 *
 * @example
 * ```vue
 * <!-- Uso diretto -->
 * <row-action
 *   icon="el-icon-edit-outline"
 *   text="Modifica"
 *   type="primary"
 *   @click="handleEdit"
 * />
 *
 * <!-- Navigazione -->
 * <row-action
 *   icon="el-icon-view"
 *   text="Visualizza"
 *   component="nuxt-link"
 *   :to="`/items/${id}`"
 * />
 * ```
 */

// Props definition - Vue 2.7 compatible syntax

export default /*#__PURE__*/_defineComponent({
  __name: 'RowAction',
  props: {
	icon: {
		type: String,
		default: '',
	},
	text: {
		type: String,
		required: true,
	},
	tooltip: {
		type: String,
		default: '',
	},
	type: {
		type: String as PropType<RowActionType>,
		default: 'default',
	},
	size: {
		type: String as PropType<RowActionSize>,
		default: 'small',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	circle: {
		type: Boolean,
		default: false,
	},
	showText: {
		type: Boolean,
		default: false,
	},
	to: {
		type: [String, Object] as PropType<string | Record<string, any>>,
		default: undefined,
	},
	component: {
		type: String as PropType<RowActionComponent>,
		default: 'el-button',
	},
},
  emits: ["click"],
  setup(__props, { emit }: { emit: ({ (e: 'click', event: Event): void }), expose: any, slots: any, attrs: any }) {

const props = __props;



// Eventi


// Computed properties
const actionProps = computed(() => {
	const computedProps: Record<string, any> = {
		type: props.component === 'el-button' ? 'text' : undefined,
		size: props.size,
		disabled: props.disabled,
		loading: props.loading,
	};

	// Props per nuxt-link
	if (props.component === 'nuxt-link' && props.to) {
		computedProps.to = props.to;
	}

	return computedProps;
});

const actionClasses = computed(() => {
	const classes = ['row-action'];

	// Applica CSS classes per tutti i tipi tranne 'default'
	if (props.type !== 'default') {
		classes.push(`row-action--${props.type}`);
	}

	if (props.circle) {
		classes.push('row-action--circle');
	}

	return classes;
});

// Event handlers
function handleClick(event: Event): void {
	if (!props.disabled) {
		emit('click', event);
	}
}

return { __sfc: true,props, emit, actionProps, actionClasses, handleClick }
}

})